import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Grid,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import PlanDialog from "./PlanDialog";
import plansApi from "../../hooks/GET/plans";

function Plan() {
  // plan list logic start
  const [planList, setPlanList] = useState([]);
  const [refetchPlanList, setRefetchPlanList] = useState(0);
  const [totalResult, setTotalResult] = useState(0);

  useEffect(() => {
    plansApi()
      .then((data) => {
        setPlanList(data);
      })
      .catch((error) => console.log(error));
  }, [refetchPlanList]);
  // plan list logic end

  //table pagination logic start
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOption = [5, 10, 20];

  const handleChangePage = (e, newPage) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, totalResult - page * rowsPerPage);

  // const emptyRows =
  //   rowsPerPage - Math.min(rowsPerPage, planList.length - page * rowsPerPage);
  //table pagination logic end

  // plan dialog logic start
  const [planDialogState, setPlanDialogState] = useState(false);

  const handlePlanDialogOpen = () => {
    setPlanDialogState(true);
  };

  const handlePlanDialogClose = (doRefetch) => {
    if (doRefetch) {
      setRefetchPlanList(refetchPlanList + 1);
    }
    setPlanDialogState(false);
  };
  // plan dialog logic end

  // search logic start
  const [search, setSearch] = useState("");

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPage(0);
  };

  const getFilteredPlanList = useMemo(() => {
    let filterdata = planList;
    filterdata = filterdata.filter((obj) =>
      JSON.stringify(obj).match(new RegExp(search, "gi"))
    );

    setTotalResult(filterdata.length);
    if (filterdata.length !== 0) {
      const lastPage = Math.ceil(filterdata.length / rowsPerPage);
      if (lastPage === page)
        setPage(lastPage - 1);
    }
    return filterdata;
  }, [planList, search, rowsPerPage, page]);
  // search logic end

  return (
    <Paper variant="outlined" sx={{ padding: 2 }}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Grid item>
          <Typography sx={{ display: "contents" }}>Show</Typography>
          <TextField
            select
            name="rowsPerPage"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            size="small"
            sx={{ verticalAlign: "middle", margin: 1, minWidth: 75 }}
          >
            {rowsPerPageOption.map((option, idx) => (
              <MenuItem key={idx} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ display: "contents" }}>entries</Typography>
          <TextField
            name="search"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search..."
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ verticalAlign: "middle", margin: 1 }}
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handlePlanDialogOpen}
          >
            Create
          </Button>
        </Grid>
      </Grid>

      {/* Table part */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">Sr. No.</TableCell>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Description</TableCell>
              <TableCell align="center">Stripe Price Id</TableCell>
              <TableCell align="center">Stripe Product Id</TableCell>
              <TableCell align="center">Price</TableCell>
              <TableCell align="center">Billing Interval</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? getFilteredPlanList.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
              : getFilteredPlanList
            ).map((fieldValue, idx) => (
              <TableRow key={idx} hover>
                <TableCell align="center">{page * rowsPerPage + idx + 1}</TableCell>
                <TableCell align="center">{fieldValue.planName}</TableCell>
                <TableCell align="center">
                  {fieldValue.planDescription}
                </TableCell>
                <TableCell align="center">
                  {fieldValue.planStripePriceId}
                </TableCell>
                <TableCell align="center">
                  {fieldValue.planStripeProductId}
                </TableCell>
                <TableCell align="center">
                  {fieldValue.planCurrecyIsoCode.toLocaleUpperCase()}{" "}
                  {fieldValue.planPrice}
                </TableCell>
                <TableCell align="center">
                  {fieldValue.planBillingInterval}
                  {`  months`}
                </TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell align="center" colSpan={7}>
                  {totalResult === 0 && "No plan available to display"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PlanDialog open={planDialogState} handleClose={handlePlanDialogClose} />

      {/* {planList.length > 0 && ( */}
      {totalResult > 0 && (
        <Grid container justifyContent="center" sx={{ mt: 2 }} spacing={1}>
          <Grid item>
            <Button
              size="small"
              onClick={(e) => handleChangePage(e, page)}
              disabled={page === 0}
            >
              Previous
            </Button>
          </Grid>
          <Grid item>
            <Pagination
              // count={Math.ceil(planList.length / rowsPerPage)}
              count={Math.ceil(totalResult / rowsPerPage)}
              page={page + 1}
              onChange={handleChangePage}
              variant="outlined"
              shape="rounded"
              color="secondary"
              hidePrevButton
              hideNextButton
            />
          </Grid>
          <Grid item>
            <Button
              size="small"
              onClick={(e) => handleChangePage(e, page + 2)}
              // disabled={Math.ceil(planList.length / rowsPerPage) === page + 1}
              disabled={Math.ceil(totalResult / rowsPerPage) === page + 1}
            >
              Next
            </Button>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default Plan;
